/**
 * 帳款報表的 query hooks(REQ-20260815002)。
 *
 * 全部唯讀,所以只有 `useQuery` 沒有 mutation。queryKey 一律以 `['ar-ap-reports', …]`
 * 開頭 —— 登錄收款 / 付款成功後用這個前綴一次失效掉所有報表
 * (見 `features/accounting/api.ts` 的 `useCreateApSettlement()`)。
 */
import { useQuery } from '@tanstack/react-query';
import type {
  AgingReport,
  ApiSuccess,
  ArApDetailReport,
  ArOverviewReport,
  MailingLabelReport,
  MonthlyStatReport,
  PartySummaryReport,
  SalesPersonSummaryReport,
  SettlementDailyReport,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

/**
 * 用 `type` 而不是 `interface`:interface 沒有隱含的 index signature,
 * 傳給下面吃 `Record<string, …>` 的 `useReport()` 會被 TS 擋下來。
 */
export type ReportRange = {
  from?: string | undefined;
  to?: string | undefined;
};

/**
 * 所有報表共用的取數函式。報表之間只差在路徑、參數與回傳型別,
 * 各寫一份 `useQuery` 只會讓 queryKey 的組法慢慢分岔。
 */
function useReport<T>(
  path: string,
  params: Record<string, string | number | undefined>,
  enabled = true,
): ReturnType<typeof useQuery<T>> {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
  );
  return useQuery<T>({
    queryKey: ['ar-ap-reports', path, clean],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<T>>(`/ar-ap-reports/${path}`, { params: clean });
      return res.data.data;
    },
  });
}

/**
 * 應收明細表 / 業務員應收明細。`salesPersonId` 與 `customerId`(REQ-20260824010)
 * 都是可選的加篩,留白就是不篩;`useReport()` 會把 undefined 的參數濾掉,
 * queryKey 也跟著不同 —— 換客戶就是換一份快取,不會拿到上一位客戶的結果。
 */
export function useArDetailReport(
  range: ReportRange,
  salesPersonId?: number | undefined,
  customerId?: number | undefined,
  enabled = true,
): ReturnType<typeof useQuery<ArApDetailReport>> {
  return useReport<ArApDetailReport>('ar-detail', { ...range, salesPersonId, customerId }, enabled);
}

export function useApDetailReport(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<ArApDetailReport>> {
  return useReport<ArApDetailReport>('ap-detail', range, enabled);
}

export function useArSummaryReport(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<PartySummaryReport>> {
  return useReport<PartySummaryReport>('ar-summary', range, enabled);
}

export function useApSummaryReport(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<PartySummaryReport>> {
  return useReport<PartySummaryReport>('ap-summary', range, enabled);
}

export function useArSalesPersonSummary(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<SalesPersonSummaryReport>> {
  return useReport<SalesPersonSummaryReport>('ar-sales-person-summary', range, enabled);
}

export function useArMonthlyStats(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<MonthlyStatReport>> {
  return useReport<MonthlyStatReport>('ar-monthly-stats', range, enabled);
}

export function useApMonthlyStats(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<MonthlyStatReport>> {
  return useReport<MonthlyStatReport>('ap-monthly-stats', range, enabled);
}

export function useArSettlementDaily(
  date: string | undefined,
  enabled = true,
): ReturnType<typeof useQuery<SettlementDailyReport>> {
  return useReport<SettlementDailyReport>('ar-settlement-daily', { date }, enabled);
}

export function useApSettlementDaily(
  date: string | undefined,
  enabled = true,
): ReturnType<typeof useQuery<SettlementDailyReport>> {
  return useReport<SettlementDailyReport>('ap-settlement-daily', { date }, enabled);
}

export function useArOverview(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<ArOverviewReport>> {
  return useReport<ArOverviewReport>('ar-overview', range, enabled);
}

export function useArAging(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<AgingReport>> {
  return useReport<AgingReport>('ar-aging', range, enabled);
}

export function useArMailingLabels(
  range: ReportRange,
  enabled = true,
): ReturnType<typeof useQuery<MailingLabelReport>> {
  return useReport<MailingLabelReport>('ar-mailing-labels', range, enabled);
}
